import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { xadmApi, type AdwordsDay, type AdwordsHour, type AdwordsSort } from '../../api/xadm.js';
import { Spinner, ErrorBox, EmptyBox, Field } from '../../components/ui.js';
import { LineChart } from '../../components/LineChart.js';
import { num, money } from '../../lib/format.js';

/**
 * 애드워즈 현황 — 원본 /home/bananapartner/xadmin/report_adw.php.
 *
 * 누적 테이블의 마지막 시간 값을 일 집계로 보는 기존 규칙과 모든 원본 지표를
 * 유지하면서, 성과 요약 → 추세 → 일자 비교 → 시간대 상세 순으로 탐색하게 한다.
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKDAY_NAMES = WEEKDAYS.map((day) => `${day}요일`);

/** 원본 :199 - 전환건이 많은 날은 눈에 띄게 (200/300/400 구간). */
function convClass(conv: number): string | undefined {
  if (conv >= 400) return 'adw-conv adw-conv--top';
  if (conv >= 300) return 'adw-conv adw-conv--high';
  if (conv >= 200) return 'adw-conv adw-conv--mid';
  return undefined;
}

const rate = (value: number | null) => (value === null ? '-' : `${value}%`);
const won = (value: number | null) => (value === null ? '-' : money(value));

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days: number) {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

export function AdwordsPage() {
  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? daysAgoISO(30);
  const to = params.get('to') ?? todayISO();
  const weekdayParam = params.get('weekday');
  const weekday = weekdayParam === null ? null : Number(weekdayParam);
  const sort = (params.get('sort') ?? 'dates') as AdwordsSort;
  const [draft, setDraft] = useState({ from, to });
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [pinnedDate, setPinnedDate] = useState<string | null>(null);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openDate = hoverDate ?? pinnedDate;

  const daily = useQuery({
    queryKey: ['xadm-adwords', from, to, weekday, sort],
    queryFn: () => xadmApi.adwords({ from, to, weekday, sort }),
  });

  const hours = useQuery({
    queryKey: ['xadm-adwords-hours', openDate],
    queryFn: () => xadmApi.adwordsHours(openDate!),
    enabled: openDate !== null,
  });

  const openDay = daily.data?.days.find((day) => day.date === openDate) ?? null;
  const summary = useMemo(() => {
    const rows = daily.data?.days ?? [];
    const chronological = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    const latest = chronological.at(-1) ?? null;
    const previous = chronological.at(-2) ?? null;
    const delta = latest && previous ? latest.conv - previous.conv : null;
    return {
      dayCount: rows.length,
      latest,
      delta,
      deltaRate: delta === null || !previous?.conv ? null : (delta / previous.conv) * 100,
      averageConv: rows.length ? Math.round((daily.data?.totals.conv ?? 0) / rows.length) : 0,
      peak: rows.length === 0 ? null : rows.reduce((max, row) => row.conv > max.conv ? row : max),
      trend: chronological,
    };
  }, [daily.data]);

  const trendLabels = summary.trend.map((day) => day.date);
  const trendSeries = [
    { id: 'conv', label: '전환', points: summary.trend.map((day) => ({ x: day.date, y: day.conv })) },
    { id: 'porder', label: '파트너 전환', points: summary.trend.map((day) => ({ x: day.date, y: day.porder })) },
  ];

  const cancelHoverClose = () => {
    if (hoverCloseTimer.current !== null) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = null;
  };

  const previewDate = (date: string) => {
    cancelHoverClose();
    setHoverDate(date);
  };

  const schedulePreviewClose = () => {
    cancelHoverClose();
    hoverCloseTimer.current = setTimeout(() => setHoverDate(null), 180);
  };

  const togglePinnedDate = (date: string) => {
    cancelHoverClose();
    setHoverDate(null);
    setPinnedDate((current) => current === date ? null : date);
  };

  function closeDetail() {
    setHoverDate(null);
    setPinnedDate(null);
  }

  function apply(next: Partial<{ weekday: number | null; sort: AdwordsSort }> = {}) {
    const query: Record<string, string> = { from: draft.from, to: draft.to };
    const nextWeekday = next.weekday === undefined ? weekday : next.weekday;
    if (nextWeekday !== null) query.weekday = String(nextWeekday);
    const nextSort = next.sort ?? sort;
    if (nextSort !== 'dates') query.sort = nextSort;
    setParams(query, { replace: true });
    closeDetail();
  }

  function reset() {
    setDraft({ from: daysAgoISO(30), to: todayISO() });
    setParams({}, { replace: true });
    closeDetail();
  }

  useEffect(() => () => cancelHoverClose(), []);

  function SortButton({ label, by }: { label: string; by: AdwordsSort }) {
    return (
      <button
        type="button"
        className="th-sort"
        aria-pressed={sort === by}
        onClick={() => apply({ sort: by })}
      >
        {label}
        <span className={sort === by ? 'th-sort__arrow th-sort__arrow--on' : 'th-sort__arrow'}>
          {sort === by ? '▼' : '↕'}
        </span>
      </button>
    );
  }

  return (
    <section className="visit-page adw-page">
      <header className="page-head visit-page__head">
        <div>
          <h1 className="page-title">애드워즈 성과</h1>
          <p className="page-desc">광고비·전환·유입과 파트너 성과를 일자별로 비교하고 시간대 변화까지 탐색합니다.</p>
        </div>
        <div className="adw-head-status">
          {daily.isFetching && <span className="page-head__busy">갱신 중…</span>}
          <span className="visit-page__source"><i aria-hidden="true" /> ADWORDS DAILY LOG</span>
        </div>
      </header>

      <section className="adw-filter" aria-label="애드워즈 조회 조건">
        <div className="adw-filter__dates">
          <Field label="시작일">
            <input
              className="input"
              type="date"
              value={draft.from}
              onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
            />
          </Field>
          <span aria-hidden="true">→</span>
          <Field label="종료일">
            <input
              className="input"
              type="date"
              value={draft.to}
              onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
            />
          </Field>
          <div className="filters__buttons">
            <button type="button" className="btn btn--primary" onClick={() => apply()}>검색</button>
            <button type="button" className="btn btn--ghost" onClick={reset}>초기화</button>
          </div>
        </div>
        <div className="adw-filter__weekdays">
          <span>요일</span>
          <div className="visit-weekdays__buttons">
            {WEEKDAYS.map((label, day) => (
              <button
                key={day}
                type="button"
                className={weekday === day ? 'visit-weekday visit-weekday--active' : 'visit-weekday'}
                aria-pressed={weekday === day}
                title={WEEKDAY_NAMES[day]}
                onClick={() => apply({ weekday: weekday === day ? null : day })}
              >
                {label}
              </button>
            ))}
          </div>
          <small>{weekday === null ? '전체 요일' : `${WEEKDAY_NAMES[weekday]}만 표시 중`}</small>
          {weekday !== null && (
            <button type="button" className="adw-filter__clear" onClick={() => apply({ weekday: null })}>요일 해제</button>
          )}
        </div>
      </section>

      {!daily.isPending && !daily.isError && summary.dayCount > 0 && (
        <>
          <div className="visit-summary adw-summary" aria-label="애드워즈 핵심 지표">
            <article className="visit-summary__item visit-summary__item--primary">
              <span>총 전환</span>
              <strong>{num(daily.data!.totals.conv)}</strong>
              <small>일평균 {num(summary.averageConv)}건 · {summary.dayCount}일 집계</small>
            </article>
            <article className="visit-summary__item adw-summary__revenue">
              <span>총 전환금액</span>
              <strong>{money(daily.data!.totals.convcost)}</strong>
              <small>평균 객단가 {won(daily.data!.totals.avgOrder)}</small>
            </article>
            <article className="visit-summary__item adw-summary__cost">
              <span>총 광고비</span>
              <strong>{money(daily.data!.totals.cost)}</strong>
              <small>광고비율 {rate(daily.data!.totals.profitRate)} · CPC {won(daily.data!.totals.clickCost)}</small>
            </article>
            <article className="visit-summary__item adw-summary__partner">
              <span>파트너 전환</span>
              <strong>{num(daily.data!.totals.porder)}</strong>
              <small>전환금액 {money(daily.data!.totals.pconvcost)} · 비율 {rate(daily.data!.totals.pProfitRate)}</small>
            </article>
          </div>

          <section className="visit-trend adw-trend" aria-label="일별 전환 추이">
            <header className="visit-trend__head">
              <div>
                <span>TREND</span>
                <h2>일별 전환 추이</h2>
                <p>{from} ~ {to}{weekday === null ? '' : ` · ${WEEKDAY_NAMES[weekday]}`} · 정렬과 무관하게 날짜순으로 표시합니다.</p>
              </div>
              {summary.peak && (
                <div className="visit-trend__peak">
                  <span>최고 전환일</span>
                  <strong>{num(summary.peak.conv)}</strong>
                  <small>{summary.peak.date}</small>
                </div>
              )}
            </header>
            <LineChart series={trendSeries} labels={trendLabels} height={220} />
          </section>
        </>
      )}

      <div className="visit-list-head">
        <div>
          <h2>일자별 광고 성과</h2>
          <p>행에 마우스를 올리면 오른쪽에서 시간대별 누적 성과와 시간당 전환을 확인할 수 있습니다.</p>
        </div>
        {!daily.isPending && !daily.isError && <span>{num(summary.dayCount)}개 일자</span>}
      </div>

      {daily.isPending ? (
        <Spinner />
      ) : daily.isError ? (
        <ErrorBox error={daily.error} onRetry={() => void daily.refetch()} />
      ) : daily.data.days.length === 0 ? (
        <EmptyBox>이 구간에는 자료가 없습니다.</EmptyBox>
      ) : (
        <div className="visit-table-wrap">
          <table className="table adw-table">
            <thead>
              <tr>
                <th aria-label="상세 열기" />
                <th><SortButton label="일자" by="dates" /></th>
                <th><SortButton label="전환" by="conv" /></th>
                <th>
                  <span className="adw-table__dual-sort">
                    <SortButton label="클릭" by="click" />
                    <SortButton label="노출" by="imp" />
                  </span>
                </th>
                <th><SortButton label="광고비" by="cost" /></th>
                <th><SortButton label="전환금액" by="convcost" /></th>
                <th><SortButton label="파트너" by="porder" /></th>
              </tr>
            </thead>
            <tbody>
              {daily.data.days.map((day) => (
                <AdwordsDayRow
                  key={day.date}
                  day={day}
                  isOpen={openDate === day.date}
                  isPinned={pinnedDate === day.date}
                  onPreview={previewDate}
                  onPreviewClose={schedulePreviewClose}
                  onToggle={togglePinnedDate}
                />
              ))}
            </tbody>
            <tfoot>
              <AdwordsTotalRow totals={daily.data.totals} />
            </tfoot>
          </table>
        </div>
      )}

      {openDate && openDay && (
        <aside
          className="visit-preview adw-preview"
          aria-label={`${openDay.date} 시간대별 애드워즈 성과`}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={schedulePreviewClose}
        >
          <section className="visit-detail">
            <header className="visit-detail__head">
              <div>
                <span>{WEEKDAY_NAMES[openDay.weekday]} · {String(openDay.lastHour).padStart(2, '0')}시 최종</span>
                <h3>{openDay.date} 시간대별 애드워즈</h3>
              </div>
              <div className="visit-detail__actions">
                <strong>전환 {num(openDay.conv)} · 광고비 {money(openDay.cost)}</strong>
                <button type="button" aria-label="시간대별 애드워즈 닫기" onClick={closeDetail}>×</button>
              </div>
            </header>
            {hours.isPending ? (
              <Spinner />
            ) : hours.isError ? (
              <ErrorBox error={hours.error} />
            ) : (
              <HourTable date={openDay.date} rows={hours.data?.hours ?? []} />
            )}
          </section>
        </aside>
      )}
    </section>
  );
}

function AdwordsDayRow({
  day,
  isOpen,
  isPinned,
  onPreview,
  onPreviewClose,
  onToggle,
}: {
  day: AdwordsDay;
  isOpen: boolean;
  isPinned: boolean;
  onPreview: (date: string) => void;
  onPreviewClose: () => void;
  onToggle: (date: string) => void;
}) {
  return (
    <tr
      className={isOpen ? 'table__row--clickable visit-table__row visit-table__row--open' : 'table__row--clickable visit-table__row'}
      onMouseEnter={() => onPreview(day.date)}
      onMouseLeave={onPreviewClose}
      onClick={() => onToggle(day.date)}
    >
      <td className="visit-table__toggle">
        <button
          type="button"
          aria-label={`${day.date} 시간대 상세 ${isPinned ? '닫기' : '고정해서 열기'}`}
          aria-expanded={isOpen}
          onClick={(event) => {
            event.stopPropagation();
            onToggle(day.date);
          }}
        >
          <span aria-hidden="true">›</span>
        </button>
      </td>
      <td>
        <div className="adw-date">
          <strong>{day.date}</strong>
          <span className={day.weekday === 0 ? 'td-danger' : day.weekday === 6 ? 'adw-sat' : undefined}>
            {WEEKDAY_NAMES[day.weekday] ?? '-'} · {String(day.lastHour).padStart(2, '0')}시 최종
          </span>
        </div>
      </td>
      <td><AdwPrimaryMetric value={day.conv} sub={`객단가 ${won(day.avgOrder)}`} highlight /></td>
      <td><AdwTrafficMetric click={day.click} imp={day.imp} clickRate={day.clickRate} clickCost={day.clickCost} /></td>
      <td><AdwPrimaryMetric value={day.cost} sub={`CPC ${won(day.clickCost)}`} moneyValue tone="cost" /></td>
      <td><AdwPrimaryMetric value={day.convcost} sub={`광고비율 ${rate(day.profitRate)}`} moneyValue tone="revenue" /></td>
      <td><AdwPartnerMetric day={day} /></td>
    </tr>
  );
}

function AdwordsTotalRow({ totals }: { totals: Omit<AdwordsDay, 'date' | 'weekday' | 'lastHour'> }) {
  return (
    <tr className="row--total adw-table__total">
      <td />
      <td><strong>조회 합계</strong></td>
      <td><AdwPrimaryMetric value={totals.conv} sub={`객단가 ${won(totals.avgOrder)}`} highlight /></td>
      <td><AdwTrafficMetric click={totals.click} imp={totals.imp} clickRate={totals.clickRate} clickCost={totals.clickCost} /></td>
      <td><AdwPrimaryMetric value={totals.cost} sub={`CPC ${won(totals.clickCost)}`} moneyValue tone="cost" /></td>
      <td><AdwPrimaryMetric value={totals.convcost} sub={`광고비율 ${rate(totals.profitRate)}`} moneyValue tone="revenue" /></td>
      <td><AdwPartnerMetric day={totals} /></td>
    </tr>
  );
}

function AdwPrimaryMetric({
  value,
  sub,
  moneyValue = false,
  highlight = false,
  tone,
}: {
  value: number;
  sub: string;
  moneyValue?: boolean;
  highlight?: boolean;
  tone?: 'cost' | 'revenue';
}) {
  return (
    <div className={`adw-primary-metric${tone ? ` adw-primary-metric--${tone}` : ''}`}>
      <strong className={highlight ? convClass(value) : undefined}>{moneyValue ? money(value) : num(value)}</strong>
      <small>{sub}</small>
    </div>
  );
}

function AdwTrafficMetric({ click, imp, clickRate, clickCost }: Pick<AdwordsDay, 'click' | 'imp' | 'clickRate' | 'clickCost'>) {
  return (
    <div className="adw-traffic">
      <span><small>클릭</small><strong>{num(click)}</strong></span>
      <span><small>노출</small><strong>{num(imp)}</strong></span>
      <em>CTR {rate(clickRate)} · CPC {won(clickCost)}</em>
    </div>
  );
}

function AdwPartnerMetric({ day }: { day: Pick<AdwordsDay, 'porder' | 'pconvcost' | 'pProfitRate' | 'pAvgOrder'> }) {
  return (
    <div className="adw-partner-metric">
      <strong>{num(day.porder)}건</strong>
      <span>{money(day.pconvcost)}</span>
      <small>비율 {rate(day.pProfitRate)} · 객단가 {won(day.pAvgOrder)}</small>
    </div>
  );
}

/** 시간당 전환은 증분, 나머지는 해당 시각까지의 기존 누적값이다. */
function HourTable({ date, rows }: { date: string; rows: AdwordsHour[] }) {
  if (rows.length === 0) return <EmptyBox>{date} 자료가 없습니다.</EmptyBox>;

  return (
    <div className="visit-detail__table-wrap">
      <table className="table table--nested adw-hour-table">
        <thead>
          <tr>
            <th>시간</th>
            <th>전환</th>
            <th>유입</th>
            <th>광고 성과</th>
            <th>파트너</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((hour) => (
            <tr key={hour.hour}>
              <td><span className="visit-hour">{String(hour.hour).padStart(2, '0')}시</span></td>
              <td>
                <div className="adw-hour-conv">
                  <strong>+{num(hour.convPerHour)}</strong>
                  <small>누적 {num(hour.conv)} · 객단가 {won(hour.avgOrder)}</small>
                </div>
              </td>
              <td><AdwTrafficMetric click={hour.click} imp={hour.imp} clickRate={hour.clickRate} clickCost={hour.click ? hour.cost / hour.click : null} /></td>
              <td>
                <div className="adw-hour-performance">
                  <span><small>광고비</small><strong>{money(hour.cost)}</strong></span>
                  <span><small>전환금액</small><strong>{money(hour.convcost)}</strong></span>
                  <em>광고비율 {rate(hour.profitRate)}</em>
                </div>
              </td>
              <td><AdwPartnerMetric day={hour} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
